import { useState, useEffect, useCallback } from 'react';

interface SidebarPreferences {
  isCollapsed: boolean;
}

const SIDEBAR_STORAGE_KEY = 'herzens-sidebar-preferences';
const MOBILE_BREAKPOINT = 768;

const isMobileWidth = () =>
  typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT;

export const useSidebar = () => {
  // Load saved preferences from localStorage
  const loadPreferences = (): SidebarPreferences => {
    try {
      const saved = localStorage.getItem(SIDEBAR_STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.warn('Failed to load sidebar preferences:', error);
    }

    // Default preferences
    return {
      isCollapsed: false,
    };
  };

  const [preferences, setPreferences] = useState<SidebarPreferences>(loadPreferences);
  // Initialise from the real width so the first paint already matches the
  // device (no desktop-layout flash on phones).
  const [isMobile, setIsMobile] = useState(isMobileWidth);

  /**
   * Mobile drawer state — deliberately NOT persisted and NOT derived from the
   * desktop `isCollapsed` preference. Those are different concepts: on desktop
   * "not collapsed" means the rail is pinned open, on mobile it would mean the
   * overlay drawer covers the screen on every page load. The drawer therefore
   * always starts closed.
   */
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Save preferences to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(preferences));
    } catch (error) {
      console.warn('Failed to save sidebar preferences:', error);
    }
  }, [preferences]);

  // Check if screen is mobile size
  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = isMobileWidth();
      setIsMobile((wasMobile) => {
        // Leaving mobile (e.g. rotating a tablet): drop the drawer so the
        // desktop rail isn't shown with a leftover overlay.
        if (wasMobile && !mobile) setIsMobileOpen(false);
        return mobile;
      });
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    window.addEventListener('orientationchange', checkScreenSize);

    return () => {
      window.removeEventListener('resize', checkScreenSize);
      window.removeEventListener('orientationchange', checkScreenSize);
    };
  }, []);

  // Lock background scrolling while the mobile drawer is open
  useEffect(() => {
    if (!isMobile || !isMobileOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isMobile, isMobileOpen]);

  // Close the mobile drawer with the Escape key
  useEffect(() => {
    if (!isMobile || !isMobileOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMobileOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isMobile, isMobileOpen]);

  // Update preferences
  const updatePreferences = useCallback((updates: Partial<SidebarPreferences>) => {
    setPreferences(prev => ({ ...prev, ...updates }));
  }, []);

  // Toggle: on mobile this opens/closes the drawer, on desktop it collapses
  // the rail to the icon-only width (which is persisted).
  const toggleCollapsed = useCallback(() => {
    if (isMobileWidth()) {
      setIsMobileOpen(open => !open);
      return;
    }
    updatePreferences({ isCollapsed: !preferences.isCollapsed });
  }, [preferences.isCollapsed, updatePreferences]);

  // Force collapse (for mobile or manual control)
  const setCollapsed = useCallback((collapsed: boolean) => {
    if (isMobileWidth()) {
      setIsMobileOpen(!collapsed);
      return;
    }
    updatePreferences({ isCollapsed: collapsed });
  }, [updatePreferences]);

  // Computed states
  const shouldShowSidebar = () => {
    // On mobile the sidebar is an overlay drawer — hidden unless opened.
    if (isMobile) return isMobileOpen;

    // Always show sidebar on desktop, but can be minimized
    return true;
  };

  const getSidebarWidth = () => {
    if (isMobile) {
      return 250; // Full width on mobile
    }

    if (preferences.isCollapsed) {
      return 60; // Minimized width
    }

    return 250; // Full width
  };

  return {
    // State
    isCollapsed: isMobile ? !isMobileOpen : preferences.isCollapsed,
    isMobile,

    // Computed
    shouldShowSidebar: shouldShowSidebar(),
    sidebarWidth: getSidebarWidth(),
    // The icon-only rail is a desktop-only mode
    isMinimized: !isMobile && preferences.isCollapsed,

    // Actions
    toggleCollapsed,
    setCollapsed,
    updatePreferences,
  };
};
